import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import {
  COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_LIST_TOTAL_QUERY,
  GET_ALLOCATION_COMPLIANCE_TOTAL_QUERY,
  GET_DUE_DILIGENCE_COMPLIANCE_RECORDS_TOTAL_QUERY
} from '@/components/compliance-page/queries';
import { PUBLISHED_INVESTMENT_MANAGER_UPDATES_INVESTMENT_IDS_QUERY } from '@/components/managerUpdates-page/queries';
import { GET_SETTINGS_QUERY } from '@/components/settings-page/queries';
import EndImpersonation from '@/components/settings-page/users/modals/endImpersonation';
import { TODO_PAGE_ALLOCATION_TASKS_REDIRECT_QUERY } from '@/components/todo-page/queries';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { DashboardNavbar } from '@/layouts/dashboard/dashboard-navbar';
import { DashboardNavbarMobile } from '@/layouts/dashboard/navbar-mobile';
import { allSidebarTabs } from '@/shared/constants/sidebarTabs';
import { SidebarTabs } from '@/shared/types/sidebarTabs';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { ROLE, TenantType } from '@trace-8/trace8-schema';

export function DashboardLayout({ children }: React.PropsWithChildren): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isDesktop, isTablet, isLargeDesktop } = useResponsive();
  const { data: customizationsData } = useCustomizationsData();
  const { setUserTasks, signInAs, user } = useAuth();

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'end-impersonation' });
  const [isImpersonation, setIsImpersonation] = useState(false);
  const [endImpersonationLoading, setEndImpersonationLoading] = useState(false);

  const { refetch: allocationTasksRefetch } = useQuery(TODO_PAGE_ALLOCATION_TASKS_REDIRECT_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setUserTasks(data?.AllocationTasks?.assignedToMeTaskCount || 0);
    }
  });

  const [moduleConfig, setModuleConfig] = useState(undefined);

  const { refetch: appSettingRefetch } = useQuery(GET_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      type: 'appSetting'
    },
    onCompleted: (data) => {
      if (data?.Settings[0]?.value) {
        setModuleConfig(JSON.parse(data?.Settings[0]?.value) ?? {});
      } else {
        setModuleConfig({});
      }
    },
    onError: (error) => {
      setModuleConfig({});
      console.error(error);
    }
  });

  const { data: allocationComplianceTotalData, loading: allocationComplianceTotalLoading } = useQuery<{
    ListAllocationCompliance: { total: number };
  }>(GET_ALLOCATION_COMPLIANCE_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      rowsPerPage: 10,
      page: 1
    }
  });

  const { data: getDueDiligenceTotalData, loading: getDueDiligenceTotalLoading } = useQuery<{
    listDueDiligenceComplianceRecords: { total: number };
  }>(GET_DUE_DILIGENCE_COMPLIANCE_RECORDS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: user?.tenant?.type === TenantType.ADVISORY_FIRM,
    variables: {
      data: {
        statusFilter: ['Approved', 'Rejected']
      }
    }
  });

  const { data: listDealDocumentsEmailTrackingTotalData, loading: listDealDocumentsEmailTrackingTotalLoading } = useQuery<{
    dealDocumentsEmailTrackingList: { total: number };
  }>(COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_LIST_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        perPage: 10,
        page: 1
      }
    }
  });

  const { data: publishedInvestmentManagerUpdatesInvestmentIdsData, loading: publishedInvestmentManagerUpdatesInvestmentIdsLoading } = useQuery(
    PUBLISHED_INVESTMENT_MANAGER_UPDATES_INVESTMENT_IDS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      skip: user?.tenant?.type === TenantType.PLATFORM_MANAGER,
      variables: {
        data: {
          publishedUpdatesOnly: true
        }
      }
    }
  );

  const getAvailableTabs = (tabs: SidebarTabs[], params: { [key: string]: boolean }): SidebarTabs[] => {
    return tabs.filter((tab) => params[tab.path]);
  };

  const filteredTabs = useMemo(() => {
    const isAdvisoryFirmUser = user && user.tenant?.type === TenantType.ADVISORY_FIRM;
    const isPlatformManagerUser = user && user.tenant?.type === TenantType.PLATFORM_MANAGER;
    const hasUserAdminOrSuperAdminRoles = user && (user.roles?.includes(ROLE.SUPER_ADMIN) || user.roles?.includes(ROLE.ADMIN));

    const showComplianceTab =
      !!allocationComplianceTotalData?.ListAllocationCompliance?.total ||
      !!getDueDiligenceTotalData?.listDueDiligenceComplianceRecords?.total ||
      !!listDealDocumentsEmailTrackingTotalData?.dealDocumentsEmailTrackingList?.total ||
      (isPlatformManagerUser && hasUserAdminOrSuperAdminRoles); // for downloading transactions investor report

    const showManagerUpdatesTab =
      isPlatformManagerUser ||
      (isAdvisoryFirmUser && publishedInvestmentManagerUpdatesInvestmentIdsData?.publishedInvestmentManagerUpdates?.length > 0);

    return getAvailableTabs(allSidebarTabs, customizationsData.menuConfig)
      .filter((tab) => {
        if (tab.path === 'compliance' && !showComplianceTab) return false;

        if (tab.path === 'managerUpdates' && !showManagerUpdatesTab) return false;

        if (
          isAdvisoryFirmUser &&
          ['dealCatalog', 'investors', 'pendingCommitments', 'transactions', 'managerUpdates', 'activeInvestments', 'compliance'].includes(tab.path)
        ) {
          return tab;
        }

        if (
          isPlatformManagerUser &&
          [
            'dealPipeline',
            'investors',
            'dueDiligence',
            'dealRegistry',
            'pendingCommitments',
            'transactions',
            'managerUpdates',
            'activeInvestments',
            'compliance'
          ].includes(tab.path)
        ) {
          return tab;
        }

        if (hasUserAdminOrSuperAdminRoles && tab.path === 'settings') {
          return tab;
        }

        if (isAdvisoryFirmUser && user.roles.includes(ROLE.ADMIN) && moduleConfig?.['Reporting'] && 'reportingData' === tab.path) {
          return tab;
        }
      })
      .map((tab) => {
        if (tab.path === 'dealCatalog') {
          return { ...tab, label: customizationsData?.theme?.catalogBanner?.pageTitle || tab.label };
        }
        return tab;
      });
  }, [
    user,
    moduleConfig,
    customizationsData,
    allocationComplianceTotalData,
    getDueDiligenceTotalData,
    listDealDocumentsEmailTrackingTotalData,
    publishedInvestmentManagerUpdatesInvestmentIdsData
  ]);

  useEffect(() => {
    if (
      !filteredTabs.length ||
      moduleConfig === undefined ||
      allocationComplianceTotalLoading ||
      getDueDiligenceTotalLoading ||
      listDealDocumentsEmailTrackingTotalLoading ||
      publishedInvestmentManagerUpdatesInvestmentIdsLoading
    )
      return;

    const currentPath = window.location.pathname;
    const hasMatchingPath = filteredTabs.find((item) => currentPath.includes(item.path));

    if (
      !hasMatchingPath &&
      !currentPath.includes('profile') &&
      !currentPath.includes('strategyDetails') &&
      !currentPath.includes('deal') &&
      // !currentPath.includes('todo') &&
      !currentPath.includes('data-integrity')
    ) {
      navigate('/404', { replace: true });
    }
  }, [
    location.pathname,
    moduleConfig,
    allocationComplianceTotalLoading,
    getDueDiligenceTotalLoading,
    listDealDocumentsEmailTrackingTotalLoading,
    publishedInvestmentManagerUpdatesInvestmentIdsLoading
  ]);

  useEffect(() => {
    appSettingRefetch();
    if (user?.impersonated) {
      setIsImpersonation(true);
    } else {
      setIsImpersonation(false);
    }
  }, [user]);

  const openModalWindow = (type: string) => {
    setEndImpersonationLoading(false);
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setEndImpersonationLoading(false);
  };

  const endImpersonation = () => {
    if (!user?.impersonated?.originalUser.email) {
      closeModalWindow();
      return;
    }

    setEndImpersonationLoading(true);
    signInAs(user.impersonated.originalUser.email)
      .then(() => {
        navigate('/settings/?action=users');
        closeModalWindow();
      })
      .catch(console.error);
  };

  return (
    <DashboardLayoutWrap>
      {modalWindow.isOpen && modalWindow.type === 'end-impersonation' && (
        <EndImpersonation
          isOpen={modalWindow.isOpen}
          onCancel={closeModalWindow}
          onConfirm={endImpersonation}
          user={user}
          signingIn={endImpersonationLoading}
        />
      )}
      {isDesktop || isLargeDesktop ? (
        <DashboardNavbar tabs={filteredTabs} menuConfig={customizationsData?.menuConfig} allocationTasksRefetch={allocationTasksRefetch} />
      ) : (
        <DashboardNavbarMobile tabs={filteredTabs} menuConfig={customizationsData?.menuConfig} allocationTasksRefetch={allocationTasksRefetch} />
      )}
      <DashboardLayoutRoot isMobile={isMobile} isTablet={isTablet}>
        {isImpersonation && (
          <Banner
            icon={<RejectedBannerIcon />}
            title={`You are impersonating ${user?.email} (${user?.tenant?.name})`}
            description={`Your access level is determined by ${user?.email}’s roles and permissions in this tenant.`}
            bgColor={themeVar('context.warning')}
            endImpersonation={() => openModalWindow('end-impersonation')}
          />
        )}
        <Outlet />
      </DashboardLayoutRoot>
    </DashboardLayoutWrap>
  );
}

const DashboardLayoutRoot = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding-left: 0;
  transition: 0.3s;
  margin-left: ${({ isMobile, isTablet }) => (isMobile || isTablet ? 0 : '80px')};
  margin-top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : 0)};
  min-height: 100vh;
  flex: 1 1 auto;
`;

const DashboardLayoutWrap = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow-x: clip;
`;
